import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, Sparkles } from 'lucide-react';
import { adminClient } from './adminApi';
import { ARCHETYPES as DEFAULT_ARCHETYPES } from '../data/archetypes';
import { RESOURCES as DEFAULT_RESOURCES } from '../data/resources';

// ---------- Schema-driven editor ----------
// Each editable field is declared so we can render labels + inputs consistently.
const ARCHETYPE_FIELDS = [
  { k: 'name', label: 'Name', type: 'text' },
  { k: 'profile', label: 'Profile line (e.g. Visual . Toward . Internal)', type: 'text' },
  { k: 'zodiac', label: 'Zodiac resonance', type: 'text' },
  { k: 'tagline', label: 'Tagline', type: 'textarea' },
  { k: 'hook', label: 'Opening hook', type: 'textarea' },
  { k: 'affirmation', label: 'Affirmation', type: 'textarea' },
  { k: 'nextStep', label: 'Your suggested next step', type: 'textarea' },
  { k: 'callFraming', label: 'Call framing (when we speak)', type: 'textarea' },
];

const RESOURCE_FIELDS = [
  { k: 'title', label: 'Toolkit title', type: 'text' },
  { k: 'subtitle', label: 'Toolkit subtitle', type: 'text' },
  { k: 'intro', label: 'Intro paragraph', type: 'textarea' },
  { k: 'closing', label: 'Closing blessing', type: 'textarea' },
];

const ARCHETYPE_ORDER = ['visionary', 'warrior', 'scholar', 'feeler', 'oracle', 'nurturer'];

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px solid rgba(184, 167, 143, 0.6)',
  background: 'rgba(255,255,255,0.7)',
  color: '#3D3830',
  fontFamily: '"Alegreya", Georgia, serif',
  fontSize: 15,
  lineHeight: 1.5,
  outline: 'none',
  resize: 'vertical',
};

const labelStyle = {
  display: 'block',
  fontSize: 11,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: '#3D3830',
  marginBottom: 6,
  fontWeight: 500,
};

function FieldEditor({ field, value, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{field.label}</label>
      {field.type === 'textarea' ? (
        <textarea
          data-testid={`field-${field.k}`}
          rows={field.rows || 3}
          style={inputStyle}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          data-testid={`field-${field.k}`}
          type="text"
          style={inputStyle}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function ArrayEditor({ label, values, onChange }) {
  const arr = Array.isArray(values) ? values : [];
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {arr.map((v, i) => (
          <textarea
            key={i}
            data-testid={`array-${label}-${i}`}
            rows={2}
            style={inputStyle}
            value={v ?? ''}
            onChange={(e) => {
              const next = arr.slice();
              next[i] = e.target.value;
              onChange(next);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function SectionsEditor({ sections, defaultSections, onChange }) {
  const list = (sections && sections.length) ? sections : defaultSections;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {list.map((s, i) => (
        <div key={i} style={{ border: '1px solid rgba(184, 167, 143, 0.4)', borderRadius: 14, padding: 16, background: 'rgba(255,255,255,0.4)' }}>
          <div style={{ ...labelStyle, color: '#3D3830', marginBottom: 10 }}>Section {i + 1}</div>
          <FieldEditor
            field={{ k: `section-${i}-label`, label: 'Section label', type: 'text' }}
            value={s.label ?? defaultSections[i]?.label}
            onChange={(v) => {
              const next = list.slice();
              next[i] = { ...next[i], label: v };
              onChange(next);
            }}
          />
          <FieldEditor
            field={{ k: `section-${i}-body`, label: 'Section body', type: 'textarea' }}
            value={s.body ?? defaultSections[i]?.body}
            onChange={(v) => {
              const next = list.slice();
              next[i] = { ...next[i], body: v };
              onChange(next);
            }}
          />
          {Array.isArray(defaultSections[i]?.prompts) && (
            <ArrayEditor
              label="Prompts / bullets"
              values={s.prompts ?? defaultSections[i]?.prompts}
              onChange={(v) => {
                const next = list.slice();
                next[i] = { ...next[i], prompts: v };
                onChange(next);
              }}
            />
          )}
          {defaultSections[i]?.note !== undefined && (
            <FieldEditor
              field={{ k: `section-${i}-note`, label: 'Closing note (italic)', type: 'textarea' }}
              value={s.note ?? defaultSections[i]?.note}
              onChange={(v) => {
                const next = list.slice();
                next[i] = { ...next[i], note: v };
                onChange(next);
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Main admin content editor ----------
export default function AdminContent() {
  const [tab, setTab] = useState('archetype');
  const [activeKey, setActiveKey] = useState('visionary');
  const [override, setOverride] = useState({});
  const [loading, setLoading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  const defaultsSource = tab === 'archetype' ? DEFAULT_ARCHETYPES : DEFAULT_RESOURCES;
  const defaults = defaultsSource[activeKey] || {};

  const merged = useMemo(() => ({ ...defaults, ...override }), [defaults, override]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await adminClient.get(`/admin/content/${tab}/${activeKey}`);
      setOverride(data.data || {});
      setSavedAt(null);
    } catch (err) {
      setError('Failed to load overrides for this item.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, activeKey]);

  const update = (key, value) => setOverride((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      await adminClient.put(`/admin/content/${tab}/${activeKey}`, { data: override });
      setSavedAt(new Date());
    } catch (err) {
      setError('Could not save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this item back to the default copy?')) return;
    setLoading(true);
    setError('');
    try {
      await adminClient.delete(`/admin/content/${tab}/${activeKey}`);
      setOverride({});
      setSavedAt(new Date());
    } catch (err) {
      setError('Could not reset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldList = tab === 'archetype' ? ARCHETYPE_FIELDS : RESOURCE_FIELDS;

  return (
    <section data-testid="admin-content" className="min-h-screen mx-auto w-full max-w-[960px] px-5 sm:px-8 pt-10 pb-16">
      {/* Header */}
      <header className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center rounded-full"
            style={{ width: 44, height: 44, backgroundColor: '#3D3830' }}>
            <Sparkles size={20} strokeWidth={1.4} color="#FFFFFF" />
          </div>
          <div>
            <p className="section-label">Admin. Content Editor</p>
            <h1 className="font-heading text-[26px] sm:text-[32px]" style={{ fontWeight: 500, color: '#3D3830' }}>
              Rewrite your copy
            </h1>
          </div>
        </div>
        <Link to="/admin" className="btn-ghost inline-flex items-center gap-2">
          <ArrowLeft size={14} strokeWidth={1.6} /> Back to dashboard
        </Link>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 mb-6" data-testid="content-tabs">
        {[
          { k: 'archetype', label: 'Archetypes (6)' },
          { k: 'resource', label: 'Toolkits (6)' },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => { setTab(t.k); setActiveKey('visionary'); }}
            className="px-4 py-2 rounded-full font-body text-[13px]"
            style={{
              backgroundColor: tab === t.k ? '#3D3830' : 'rgba(206,197,212,0.22)',
              color: tab === t.k ? '#FFFFFF' : '#3D3830',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontWeight: 500,
              border: '1px solid transparent',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-[220px_1fr] gap-6">
        {/* Archetype picker */}
        <aside className="flex flex-row sm:flex-col gap-2 overflow-x-auto" data-testid="content-picker">
          {ARCHETYPE_ORDER.map((k) => {
            const obj = defaultsSource[k];
            if (!obj) return null;
            const label = tab === 'archetype' ? obj.name : obj.title;
            const active = activeKey === k;
            return (
              <button
                key={k}
                data-testid={`pick-${k}`}
                onClick={() => setActiveKey(k)}
                className="text-left px-4 py-3 rounded-xl font-body text-[14px]"
                style={{
                  backgroundColor: active ? 'rgba(158,140,168,0.22)' : 'rgba(255,255,255,0.55)',
                  border: `1px solid ${active ? '#3D3830' : 'rgba(206,197,212,0.45)'}`,
                  color: '#3D3830',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            );
          })}
        </aside>

        {/* Editor */}
        <div data-testid="content-editor">
          {error && (
            <div className="mb-4 rounded-xl px-4 py-3 font-body text-[13px]"
              style={{ backgroundColor: 'rgba(177,75,75,0.08)', color: '#B14B4B' }}>
              {error}
            </div>
          )}

          {loading && (
            <p className="font-body text-[13px] mb-4" style={{ color: '#3D3830' }}>Loading…</p>
          )}

          {fieldList.map((f) => (
            <FieldEditor
              key={f.k}
              field={f}
              value={override[f.k] ?? defaults[f.k]}
              onChange={(v) => update(f.k, v)}
            />
          ))}

          {/* Offering sub-object for archetypes */}
          {tab === 'archetype' && defaults.offering && (
            <div style={{ marginTop: 8, marginBottom: 16, border: '1px solid rgba(184, 167, 143, 0.4)', borderRadius: 14, padding: 16, background: 'rgba(255,255,255,0.4)' }}>
              <div style={{ ...labelStyle, color: '#3D3830', marginBottom: 10 }}>Offering</div>
              <FieldEditor
                field={{ k: 'offering-cta', label: 'CTA button text', type: 'text' }}
                value={(override.offering?.cta) ?? defaults.offering?.cta}
                onChange={(v) => update('offering', { ...defaults.offering, ...(override.offering || {}), cta: v })}
              />
              <FieldEditor
                field={{ k: 'offering-desc', label: 'Offering description', type: 'textarea' }}
                value={(override.offering?.desc) ?? defaults.offering?.desc}
                onChange={(v) => update('offering', { ...defaults.offering, ...(override.offering || {}), desc: v })}
              />
            </div>
          )}

          {/* Sections editor */}
          {Array.isArray(defaults.sections) && (
            <div style={{ marginTop: 12 }}>
              <div style={{ ...labelStyle, marginBottom: 10 }}>Sections</div>
              <SectionsEditor
                sections={override.sections || defaults.sections}
                defaultSections={defaults.sections}
                onChange={(v) => update('sections', v)}
              />
            </div>
          )}

          {/* Save row */}
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button
              data-testid="content-save-button"
              onClick={handleSave}
              disabled={loading}
              className="btn-primary"
            >
              <Save size={14} strokeWidth={1.6} />
              {loading ? 'Saving…' : 'Save changes'}
            </button>
            <button
              data-testid="content-reset-button"
              onClick={handleReset}
              disabled={loading}
              className="btn-ghost inline-flex items-center gap-2"
            >
              <RotateCcw size={14} strokeWidth={1.6} /> Reset to default
            </button>
            {savedAt && (
              <span className="font-body text-[13px]" style={{ color: '#3D3830' }}>
                Saved just now. Refresh the quiz page to see it live.
              </span>
            )}
          </div>

          {/* Preview note */}
          <p className="font-body text-[13px] mt-6" style={{ color: '#6d655a' }}>
            Your edits are stored as overrides on top of the built-in defaults.{' '}
            <em>Reset to default</em> removes your override and restores the original copy instantly.
          </p>
        </div>
      </div>
    </section>
  );
}
